import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatPrice, formatApiError, saveOrderToken, saveLastOrderNumber, walletBrand,
              getLastAddress, saveLastAddress } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { AddressFields } from "../components/AddressFields";
import { PhoneInput } from "../components/PhoneInput";
import { azLocalDigits, isAzLocalComplete, toE164 } from "../lib/phone";

// Vaxt istəyi çipləri — serverdəki `DeliveryPref` ilə eyni açarlar.
const PREFS = ["asap", "today", "tomorrow", "evening", "call"];

const Checkout = () => {
  const { items, subtotal, clear, refreshStock, hasSoldOut } = useCart();
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [epointEnabled, setEpointEnabled] = useState(false);
  // Apple Pay / Google Pay — epoint tərəfindən aktivləşdirildikdə görünür.
  const [walletEnabled, setWalletEnabled] = useState(false);
  // Cihaza uyğun ad: iPhone-da "Apple Pay", Android-də "Google Pay".
  const [brand] = useState(walletBrand);
  const [wallet, setWallet] = useState(null); // {url, origin, order}
  // Yeganə ödəniş üsulu kartdır — seçim yoxdur.
  const method = "epoint";
  const [submitting, setSubmitting] = useState(false);
  // Gözləmə 5 saniyəni keçəndə izah çıxır. Ölçülüb (2026-09-22): Epoint bəzən
  // 12–60 saniyə cavab verir, müştəri isə ~40 saniyədə səhifəni atıb yenidən
  // cəhd edirdi — atılmış sifariş stoku tutur, təkrar cəhd «stok yoxdur» alır.
  // Ona görə mətn məhz «səhifəni yeniləməyin» deyir.
  const [slowHint, setSlowHint] = useState(false);
  useEffect(() => {
    if (!submitting) { setSlowHint(false); return undefined; }
    const timer = setTimeout(() => setSlowHint(true), 5000);
    return () => clearTimeout(timer);
  }, [submitting]);
  // Ödənişdən əvvəl yalnız ad və telefon soruşulur — ünvan onsuz da
  // telefonla dəqiqləşdirilir, artıq xanalar sifarişdən imtinaya səbəb olur.
  const [form, setForm] = useState({ name: "", phone: "" });
  // Daimi müştəri endirimi: telefon nömrəsinə bağlıdır.
  const [discount, setDiscount] = useState({ found: false, percent: 0 });

  /* ÇATDIRILMA (Wolt, təsisçi qərarı 2026-10-02).
     Wolt açıqdırsa ünvan checkout-da soruşulur və haqq EYNİ ödənişə düşür —
     əvvəl ayrıca linklə ödənirdi və bir müştəri onu 6 cəhddə ödəmişdi.
     Wolt söndürülübsə forma köhnə kimidir: yalnız ad və telefon.
       wolt   — Wolt kuryeri, haqq serverdəki qiymətdən (`quote_id`)
       manual — Wolt bu ünvana qiymət verə bilmədi: köhnə yol, ünvan
                saxlanılır, haqqı sahibkar sonra ayrıca linklə göndərir
     «Mağazadan götürəcəyəm» seçimi ÇIXARILIB (təsisçi qərarı, 2026-10-21):
     saytda yalnız çatdırılma var. Backend `pickup`-ı köhnə sifarişlər
     üçün hələ qəbul edir. */
  const [woltEnabled, setWoltEnabled] = useState(false);
  // Wolt müştəriyə izləmə linkini SMS ilə göndərirmi (serverdən — ayarla dəyişir).
  const [woltSms, setWoltSms] = useState(false);
  const [delivery, setDelivery] = useState("manual");
  const [addr, setAddr] = useState({ street: "", flat: "", note: "" });
  // Mağaza paneldə çatdırılmanı «bağlı» edibsə müştəri yenə sifariş verir,
  // amma vəd əvəzinə «sizinlə əlaqə saxlanılacaq» görür (təsisçi, 2026-10-20).
  const [deliveryOnline, setDeliveryOnline] = useState(true);
  // Vaxt istəyi — Wolt-a getmir, sahibkar paneldə görür.
  const [pref, setPref] = useState("asap");
  // Hədiyyə: bağlamanı başqa adam alır — kuryerin zəngi və SMS ona yönəlir.
  const [gift, setGift] = useState({ on: false, name: "", phone: "", sms: true });
  /* SON ÜNVAN (təsisçi qərarı, 2026-10-20): girişli müştəridə əvvəlki
     sifarişlərin ünvanları serverdən, qonaqda bu cihazın yaddaşından gəlir
     və forma hazır dolur. Nişan da yadda qalır (`initialPin`). */
  const [savedList, setSavedList] = useState([]);   // hesabdakı ünvanlar
  const [savedFrom, setSavedFrom] = useState(null);  // "account" | "device" | null
  const [initialPin, setInitialPin] = useState(null);
  const [formKey, setFormKey] = useState(0);         // AddressFields-i sıfırlamaq üçün
  const prefilled = useRef(false);
  const pinRef = useRef(null);
  // idle | loading | ready | unavailable | error
  const [quote, setQuote] = useState({ status: "idle" });
  // Qiyməti məcburi yeniləmək üçün (server «müddəti bitib» deyəndə).
  const [quoteNonce, setQuoteNonce] = useState(0);

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setEpointEnabled(data.epoint_configured);
      setWalletEnabled(!!data.wallet_enabled);
      setWoltEnabled(!!data.wolt_enabled);
      setWoltSms(!!data.wolt_sms);
      setDeliveryOnline(data.delivery_online !== false);
      setDelivery(data.wolt_enabled ? "wolt" : "manual");
    }).catch(() => {});
    // Səbətdən bura keçənə qədər məhsul satıla bilər — stoku təzələyirik.
    refreshStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "", phone: azLocalDigits(user.phone || "") }));
  }, [user]);

  /* Yadda qalan ünvanı formaya qoyur. AddressFields `key` ilə yenidən
     qurulur ki, nişan təzə `initialPin`-dən başlasın. */
  const applySaved = useCallback((a, source) => {
    prefilled.current = true;
    setAddr({ street: a.street || "", flat: a.flat || "", note: a.note || "" });
    setPref(PREFS.includes(a.pref) ? a.pref : "asap");
    const pinned = a.lat != null && a.lon != null
      && (a.coord_source === "gps" || a.coord_source === "map");
    setInitialPin(pinned ? { lat: a.lat, lon: a.lon, source: a.coord_source } : null);
    setSavedFrom(source);
    setFormKey((k) => k + 1);
  }, []);

  const resetAddress = () => {
    setAddr({ street: "", flat: "", note: "" });
    setInitialPin(null);
    setSavedFrom(null);
    setFormKey((k) => k + 1);
  };

  // Girişli müştəri: hesabın ünvanları (serverdən); yoxdursa və ya qonaqdırsa
  // bu cihazın yaddaşı. Giriş vəziyyəti bilinənə qədər gözlənilir ki, qonaq
  // yolu hesabın ünvanlarını qabaqlamasın.
  useEffect(() => {
    if (!woltEnabled || authLoading || prefilled.current) return undefined;
    let alive = true;
    const fromDevice = () => {
      const a = getLastAddress();
      if (alive && a && !prefilled.current) applySaved(a, "device");
    };
    if (user) {
      api.get("/my/addresses").then(({ data }) => {
        if (!alive || prefilled.current) return;
        if (Array.isArray(data) && data.length) {
          setSavedList(data);
          applySaved(data[0], "account");
        } else {
          fromDevice();
        }
      }).catch(fromDevice);
    } else {
      fromDevice();
    }
    return () => { alive = false; };
  }, [woltEnabled, authLoading, user, applySaved]);

  /* Müştəri telefonunu yazdıqca endirim kartı axtarılır. Yazarkən hər hərfə
     sorğu getməsin deyə 500 ms gözlənilir. Bu YALNIZ göstərmək üçündür —
     sifarişin real endirimi serverdə yenidən hesablanır. */
  useEffect(() => {
    if (!isAzLocalComplete(form.phone)) {
      setDiscount({ found: false, percent: 0 });
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      api.post("/discount/lookup", { phone: toE164(form.phone) })
        .then(({ data }) => { if (alive) setDiscount(data); })
        .catch(() => { if (alive) setDiscount({ found: false, percent: 0 }); });
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [form.phone]);


  const discountAmount = discount.percent > 0
    ? Math.round(subtotal * discount.percent) / 100
    : 0;
  const productsTotal = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
  // Endirim yalnız məhsula aiddir — çatdırılma haqqı Wolt-un qiymətidir.
  const deliveryFee = woltEnabled && delivery === "wolt" && quote.status === "ready" ? quote.fee : 0;
  const total = Math.round((productsTotal + deliveryFee) * 100) / 100;
  // Wolt bu ünvana çatdıra bilmirsə müştəriyə başqa yol göstərilir —
  // 15 km-dən uzaq ünvan İSTİSNADIR (təsisçi, 2026-10-21): çatdırılma yoxdur.
  const needsAlternative =
    (quote.status === "unavailable" && quote.kind !== "address" && quote.kind !== "far")
    || quote.status === "error";
  const waitingQuote = woltEnabled && delivery === "wolt" && quote.status !== "ready";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  /**
   * Sifarişi yaradır və ödənişə keçir.
   *
   * mode = "card"   → epoint-in kart səhifəsinə yönləndirilir
   * mode = "wallet" → Apple/Google Pay pəncərəsi elə bu səhifədə açılır
   *
   * Hər iki halda sifariş EYNİ şəkildə yaradılır və ödənişin doğruluğu
   * serverdə (callback + get-status) yoxlanılır.
   */
  const placeOrder = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(lang === "en" ? "Please fill required fields" : "Zəhmət olmasa ad və telefon nömrəsini yazın");
      return;
    }
    // Ünvan soruşulmadığına görə nömrə yeganə əlaqə yoludur — səhv nömrə
    // sifarişin çatdırılmaması deməkdir.
    if (!isAzLocalComplete(form.phone)) {
      toast.error(lang === "en" ? "Please enter a valid phone number" : "Telefon nömrəsi tam yazılmayıb (+994 və 9 rəqəm)");
      return;
    }
    if (hasSoldOut) {
      toast.error(t("remove_sold_to_continue"));
      return;
    }
    // Hədiyyədə kuryer ALICIYA zəng edir — onun nömrəsi də tam olmalıdır.
    if (woltEnabled && gift.on
        && (!gift.name.trim() || !isAzLocalComplete(gift.phone))) {
      toast.error(t("gift_required"));
      return;
    }
    if (woltEnabled && delivery === "wolt" && quote.status !== "ready") {
      toast.error(t("quote_need"));
      return;
    }
    setSubmitting(true);
    // Pəncərə DƏRHAL açılır. Əvvəl müştəri iki sorğu bitənə qədər (sifariş +
    // cüzdan) ekranda yalnız "Yüklənir" görürdü və nə baş verdiyini bilmirdi.
    // İndi ilk toxunuşdan sonra dərhal görünən cavab var.
    if (walletEnabled) setWallet({ status: "loading" });
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity, size: i.size, color: i.color })),
        customer: { name: form.name.trim(), phone: toE164(form.phone) },
        payment_method: method,
      };
      if (woltEnabled && delivery === "wolt") {
        // Küçə və haqq serverdəki qiymətdən gəlir; qeydlər isə buradan, çünki
        // qiymət alınandan sonra da yazıla bilər.
        payload.delivery_method = "wolt";
        payload.quote_id = quote.quote_id;
        payload.address = { flat: addr.flat.trim(), note: addr.note.trim() };
      } else if (woltEnabled && addr.street.trim()) {
        // Telefonla razılaşma: yazılmış ünvan itməsin — sahibkar zəng etmədən görür.
        payload.address = { city: "Bakı", line: addr.street.trim(),
                            flat: addr.flat.trim(), note: addr.note.trim() };
      }
      if (woltEnabled) {
        payload.delivery_pref = pref;
        if (gift.on) {
          payload.recipient = { name: gift.name.trim(), phone: toE164(gift.phone), sms: gift.sms };
        }
      }
      const { data } = await api.post("/orders", payload);
      const order = data.order;
      // Ödəniş yönləndirməsindən əvvəl saxlanılır — səhifədən çıxsaq da qalır.
      saveOrderToken(order.order_number, data.access_token);
      saveLastOrderNumber(order.order_number);
      if (woltEnabled && addr.street.trim()) {
        // Növbəti sifarişdə ünvan hazır gəlsin. Nişan müştərinin özündəndirsə
        // o da qalır; Wolt-un tanıdığı yer isə küçədən yenidən tapılır.
        const p = pinRef.current;
        saveLastAddress({
          street: addr.street.trim(), flat: addr.flat.trim(), note: addr.note.trim(), pref,
          lat: p ? p.lat : null, lon: p ? p.lon : null, coord_source: p ? p.source : "wolt",
        });
      }

      // Cüzdan varsa ödəniş üsulu seçimi pəncərəsi açılır (Apple/Google Pay
      // + kart). Cüzdan yoxdursa boş yerə addım əlavə etmirik — müştəri
      // birbaşa kart səhifəsinə keçir.
      if (walletEnabled) {
        try {
          const { data: w } = await api.get(`/orders/${order.order_number}/wallet`,
            { params: { token: data.access_token } });
          if (w.available && w.widget_url) {
            setWallet({ status: "ready", url: w.widget_url, origin: w.origin,
                        order: order.order_number,
                        cardUrl: data.payment?.redirect_url || "" });
            return;
          }
        } catch {
          /* aşağıda kart səhifəsinə keçirik */
        }
      }

      if (data.payment && data.payment.redirect_url) {
        window.location.href = data.payment.redirect_url;
        return;
      }
      setWallet(null);
      clear();
      navigate(`/order/${order.order_number}`);
    } catch (err) {
      setWallet(null);
      if (err.response?.status === 409 && delivery === "wolt") {
        // Qiymət köhnəlib: yenisini soruşuruq, müştəri yeni məbləği görüb basır.
        setQuoteNonce((n) => n + 1);
        toast.error(t("quote_refreshed"));
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    placeOrder();
  };

  /* Apple/Google Pay pəncərəsi epoint-in öz səhifəsidir və nəticəni
     `postMessage` ilə bildirir.
     ⚠️ TƏHLÜKƏSİZLİK: mesaj YALNIZ epoint-in ünvanından qəbul edilir —
     əks halda istənilən sayt "ödənildi" deyə bilərdi. Onsuz da bu mesaj
     sifarişi ödənilmiş saymır: real status serverdən oxunur. */
  const onWalletMessage = useCallback((event) => {
    if (!wallet || wallet.status !== "ready" || event.origin !== wallet.origin) return;
    let d = event.data;
    if (typeof d === "string") {
      try { d = JSON.parse(d); } catch { return; }
    }
    const status = d && d.status;
    if (status === "success") {
      clear();
      navigate(`/payment-success?order=${wallet.order}`);
    } else if (status === "error" || status === "failed" || status === "declined") {
      setWallet(null);
      toast.error(t("pay_fail_title"));
    }
  }, [wallet, clear, navigate, t]);

  useEffect(() => {
    if (!wallet || wallet.status !== "ready") return;
    window.addEventListener("message", onWalletMessage);
    return () => window.removeEventListener("message", onWalletMessage);
  }, [wallet, onWalletMessage]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl font-light mb-6">{t("cart_empty")}</h1>
        <button onClick={() => navigate("/shop")} className="bg-brand-ink text-white px-8 py-4 text-sm tracking-[0.12em] uppercase">{t("continue_shopping")}</button>
      </div>
    );
  }

  const field = "w-full border border-brand-line bg-white px-4 py-3 text-sm outline-none focus:border-brand-ink transition-colors";
  const labelCls = "overline block mb-2";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <h1 className="font-serif font-light text-5xl tracking-tight mb-12">{t("checkout_title")}</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-10">
          {/* Contact */}
          <div>
            <h2 className="font-serif text-2xl mb-5">{t("contact_info")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>{t("full_name")} *</label>
                <input className={field} value={form.name} onChange={set("name")} data-testid="checkout-name" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>{t("phone")} *</label>
                <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="checkout-phone" />
                {discount.found && discount.percent > 0 && (
                  <p className="text-xs text-brand-oxid mt-2" data-testid="checkout-discount-note">
                    ✦ {t("discount_card_found")} — {discount.percent}%
                  </p>
                )}
              </div>
            </div>
            {!woltEnabled && (
              <p className="text-xs text-brand-muted mt-4" data-testid="delivery-note">
                {t("delivery_by_phone")}
              </p>
            )}
          </div>

          {woltEnabled && (
            <div data-testid="delivery-section">
              <h2 className="font-serif text-2xl mb-5">{t("delivery_section_title")}</h2>
              <div className="space-y-3">
                {[
                  { v: "wolt", title: t("delivery_wolt"),
                    note: deliveryOnline ? t("delivery_wolt_note") : t("delivery_offline_note") },
                  // Telefonla razılaşma yalnız Wolt çatdıra bilməyəndə görünür —
                  // hər zaman görünsə müştəri lazımsız yerə ikinci ödənişə düşərdi.
                  ...(needsAlternative || delivery === "manual"
                    ? [{ v: "manual", title: t("delivery_manual"), note: t("delivery_manual_note") }]
                    : []),
                ].map((o) => (
                  <label
                    key={o.v}
                    data-testid={`delivery-opt-${o.v}`}
                    className={`flex items-start gap-3 border px-4 py-3 cursor-pointer transition-colors ${delivery === o.v ? "border-brand-ink bg-white" : "border-brand-line hover:border-brand-sub"}`}
                  >
                    <input
                      type="radio"
                      name="delivery"
                      value={o.v}
                      checked={delivery === o.v}
                      onChange={() => setDelivery(o.v)}
                      className="mt-1 accent-[#121212]"
                    />
                    <span>
                      <span className="block text-sm">{o.title}</span>
                      <span className="block text-xs text-brand-muted mt-0.5">{o.note}</span>
                    </span>
                  </label>
                ))}
              </div>

              {(
                <>
                  {/* Yadda qalan ünvan: hazır dolub, bir toxunuşla təmizlənir. */}
                  {savedFrom && (
                    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-brand-muted"
                         data-testid="saved-address-banner">
                      <span>✓ {t("saved_filled")}</span>
                      <button type="button" onClick={resetAddress} data-testid="saved-address-other"
                        className="underline underline-offset-4 hover:text-brand-ink">
                        {t("saved_other")}
                      </button>
                    </div>
                  )}
                  {savedList.length > 1 && (
                    <div className="mt-3" data-testid="saved-address-list">
                      <p className="text-xs text-brand-muted mb-1.5">{t("saved_pick")}:</p>
                      <div className="flex flex-wrap gap-2">
                        {savedList.map((a, i) => (
                          <button type="button" key={i} onClick={() => applySaved(a, "account")}
                            className={`border px-3 py-1.5 text-xs ${addr.street === a.street && (addr.flat || "") === (a.flat || "") ? "border-brand-ink" : "border-brand-line hover:border-brand-ink"}`}>
                            {a.street}{a.flat ? ` · ${a.flat}` : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <AddressFields
                    key={formKey}
                    wolt={delivery === "wolt"}
                    addr={addr}
                    setAddr={setAddr}
                    quote={quote}
                    setQuote={setQuote}
                    nonce={quoteNonce}
                    sms={woltSms}
                    offline={!deliveryOnline}
                    initialPin={initialPin}
                    onPinChange={(p) => { pinRef.current = p; }}
                    needsAlternative={needsAlternative}
                    onAlternative={setDelivery}
                  />

                  {/* Hədiyyə — bağlamanı sifarişçi yox, başqa adam alır. */}
                  <div className="mt-5 border border-brand-line bg-white p-4" data-testid="gift-box">
                    <label className="flex items-center gap-3 text-sm cursor-pointer">
                      <input type="checkbox" checked={gift.on}
                        onChange={(e) => setGift({ ...gift, on: e.target.checked })}
                        className="accent-[#121212]" data-testid="gift-toggle" />
                      {t("gift_toggle")}
                    </label>
                    {gift.on && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className={labelCls}>{t("gift_name")} *</label>
                          <input className={field} value={gift.name} maxLength={100}
                            onChange={(e) => setGift({ ...gift, name: e.target.value })}
                            data-testid="gift-name" />
                        </div>
                        <div>
                          <label className={labelCls}>{t("gift_phone")} *</label>
                          <PhoneInput value={gift.phone} onChange={(v) => setGift({ ...gift, phone: v })} testid="gift-phone" />
                        </div>
                        {woltSms && delivery === "wolt" && (
                          <label className="sm:col-span-2 flex items-start gap-3 text-sm cursor-pointer">
                            <input type="checkbox" checked={gift.sms}
                              onChange={(e) => setGift({ ...gift, sms: e.target.checked })}
                              className="mt-0.5 accent-[#121212]" data-testid="gift-sms" />
                            <span>
                              {t("gift_sms")}
                              <span className="block text-xs text-brand-muted mt-0.5">{t("gift_sms_note")}</span>
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Vaxt istəyi — kuryeri sahibkar çağırır, istək ona çatır. */}
                  <div className="mt-5" data-testid="pref-box">
                    <p className="overline mb-2">{t("pref_title")}</p>
                    <div className="flex flex-wrap gap-2">
                      {PREFS.map((p) => (
                        <button type="button" key={p} onClick={() => setPref(p)} data-testid={`pref-${p}`}
                          aria-pressed={pref === p}
                          className={`border px-3 py-2 text-xs transition-colors ${pref === p ? "border-brand-ink bg-brand-ink text-white" : "border-brand-line hover:border-brand-ink"}`}>
                          {t(`pref_${p}`)}
                        </button>
                      ))}
                    </div>
                    {/* Bağlı olanda izah artıq yuxarıda (seçim + qiymət qutusu) var. */}
                    {deliveryOnline && (
                      <p className="text-xs text-brand-muted mt-2">{t("pref_note")}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-5">
          <div className="border border-brand-line p-8 sticky top-28">
            <h2 className="font-serif text-2xl mb-6">{t("order_summary")}</h2>
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <div className="w-14 h-16 bg-brand-bg2 overflow-hidden shrink-0">
                    <img src={imgUrl(it.image)} alt={it.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = imgUrl(null); }} />
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="font-serif text-base leading-tight">{lang === "en" ? it.name_en || it.name : it.name}</p>
                    <p className="text-xs text-brand-muted mt-0.5">×{it.quantity}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}</p>
                  </div>
                  <p className="text-sm">{formatPrice(it.price * it.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-brand-line mt-6 pt-6 space-y-3">
              {discountAmount > 0 || woltEnabled ? (
                <>
                  <div className="flex justify-between items-center text-sm">
                    <span className="overline">{t("subtotal")}</span>
                    <span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between items-center text-sm text-brand-oxid">
                      <span className="overline">
                        {t("discount")} · {discount.percent}%
                        {discount.card_number ? ` · ${discount.card_number}` : ""}
                      </span>
                      <span data-testid="checkout-discount">−{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  {woltEnabled && (
                    <div className="flex justify-between items-center text-sm" data-testid="checkout-delivery-line">
                      <span className="overline">
                        {t("delivery_fee_label")}
                      </span>
                      <span>
                        {delivery === "manual"
                          ? t("delivery_later")
                          : quote.status === "ready" ? formatPrice(quote.fee) : "—"}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t border-brand-line">
                    <span className="overline">{t("order_total")}</span>
                    <span className="font-serif text-2xl" data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="overline">{t("subtotal")}</span>
                  <span className="font-serif text-2xl" data-testid="checkout-total">{formatPrice(subtotal)}</span>
                </div>
              )}
            </div>
            {/* Xəbərdarlıqlar düymələrin yanındadır — müştəri düyməni basmazdan
                əvvəl niyə işləmədiyini elə orada görməlidir. */}
            {!epointEnabled && (
              <p className="text-xs text-destructive mt-4" data-testid="pay-unavailable">
                {t("pay_unavailable")}
              </p>
            )}
            {hasSoldOut && (
              <p className="text-xs text-destructive mt-4" data-testid="checkout-sold-warning">
                {t("remove_sold_to_continue")}
              </p>
            )}
            {waitingQuote && !hasSoldOut && (
              <p className="text-xs text-brand-muted mt-4" data-testid="checkout-quote-hint">
                {t("quote_need")}
              </p>
            )}
            {/* TƏK düymə. Ödəniş üsulunun seçimi açılan pəncərədədir —
                əvvəl müştəri eyni seçimi iki dəfə görürdü. */}
            <button
              type="submit"
              disabled={submitting || !epointEnabled || hasSoldOut || waitingQuote}
              data-testid="place-order-btn"
              className="w-full mt-6 h-[52px] rounded-[10px] bg-brand-ink text-white text-sm tracking-[0.15em] uppercase hover:bg-brand-oxid transition-colors disabled:opacity-50"
            >
              {submitting ? t("loading") : t("pay_now")}
            </button>
            {submitting && slowHint && !wallet && (
              <p className="mt-3 text-xs text-brand-sub text-center" data-testid="payment-slow-hint">
                {t("payment_slow_hint")}
              </p>
            )}
            {walletEnabled && (
              <p className="mt-3 text-xs text-brand-muted text-center" data-testid="accepted-methods">
                {brand} · Visa · Mastercard
              </p>
            )}
          </div>
        </div>
      </form>

      {/* Apple / Google Pay pəncərəsi. Məzmun epoint-in öz səhifəsidir —
          kart məlumatları heç vaxt bizim saytdan keçmir. */}
      {/* ÖDƏNİŞ ÜSULU SEÇİMİ.
          Sifariş artıq yaradılıb — burada müştəri yalnız necə ödəyəcəyini seçir.
          Apple/Google Pay düyməsi epoint-in çərçivəsindən gəlir: Apple Pay
          pəncərəsini yalnız həmin çərçivədəki düymə aça bilər (Apple-ın
          təhlükəsizlik qaydası), kənardan proqramla işə salmaq mümkün deyil. */}
      {wallet && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          data-testid="wallet-modal"
        >
          <div className="bg-white w-full max-w-md rounded-[10px] overflow-hidden">
            <div className="flex items-start justify-between border-b border-brand-line px-5 py-4">
              <div>
                <p className="text-sm font-medium">{t("choose_payment")}</p>
                <p className="font-serif text-2xl mt-1" data-testid="wallet-amount">
                  {formatPrice(total)}
                </p>
              </div>
              {wallet.status === "ready" && (
                <button
                  type="button"
                  onClick={() => setWallet(null)}
                  data-testid="wallet-close"
                  className="text-brand-muted hover:text-brand-ink -mr-1"
                  aria-label={t("close")}
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
              )}
            </div>

            {wallet.status === "loading" ? (
              /* Sifariş hazırlanır. Boş gözləmə əvəzinə görünən proses —
                 müştəri nəyi gözlədiyini bilir. */
              <div className="px-5 py-12 flex flex-col items-center gap-3" data-testid="wallet-loading">
                <Loader2 size={26} strokeWidth={1.5} className="animate-spin text-brand-muted" />
                <p className="text-sm text-brand-sub">{t("preparing_payment")}</p>
                {slowHint && (
                  <p className="text-xs text-brand-muted text-center px-4" data-testid="wallet-slow-hint">
                    {t("payment_slow_hint")}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="px-5 pt-5 pb-1">
                  {/* epoint-in düyməsi 35px + boşluqlar ≈ 45px. Çərçivə qısa
                      saxlanılır: cihazda cüzdan yoxdursa səhifə boş qalır və
                      hündür çərçivə böyük boşluq kimi görünərdi. */}
                  <iframe
                    src={wallet.url}
                    title={brand}
                    className="w-full block"
                    style={{ height: 64, border: 0 }}
                    allow="payment"
                  />
                </div>

                <div className="flex items-center gap-3 px-5 my-2">
                  <span className="h-px flex-1 bg-brand-line" />
                  <span className="text-xs text-brand-muted uppercase tracking-wider">{t("or")}</span>
                  <span className="h-px flex-1 bg-brand-line" />
                </div>

                <div className="px-5 pt-2 pb-5">
                  <button
                    type="button"
                    onClick={() => { window.location.href = wallet.cardUrl; }}
                    data-testid="wallet-card-btn"
                    className="w-full h-[52px] border border-brand-ink rounded-[10px] text-sm tracking-[0.15em] uppercase hover:bg-brand-bg2 transition-colors"
                  >
                    {t("pay_with_card")}
                  </button>
                </div>
              </>
            )}

            <div className="border-t border-brand-line px-5 py-4">
              <p className="text-xs text-brand-muted">{t("wallet_hint")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
